import { logger } from "@/utils/monitoring/logging"

import { dexie } from "../../db"
import { logDexieError } from "../../utils/logDexieError"

/**
 * Local mirror of a claim detail photo — a close-up shot attached to a claim marker.
 * Maps to the `session_claim_detail` table.
 * Metadata-only (no binary) so list/thumbnail queries stay light — the binary lives in
 * `claimDetailBlobs` and is loaded lazily, mirroring the photos/photoBlobs split.
 */
export type ClaimDetailLocal = {
  dexieId: number
  id: string
  sessionId: string
  /** FK -> claimMarkers.id the detail belongs to */
  markerId: string
  createdAt: Date
  updatedAt: Date
  deleted?: boolean
  /** Whether a local binary exists in claimDetailBlobs */
  hasData: boolean
  /** Server presigned URL — used to render details hydrated from the server (no local binary) */
  url?: string
  /** Set once the detail has been synced to the backend */
  uploadedAt?: Date
  uploadFailed?: boolean
  /** Set when a server delete-sync attempt failed */
  deleteFailed?: boolean
}

/** Binary detail payload stored in its own table (claimDetailBlobs); `detailId` is a unique 1:1 ref to ClaimDetailLocal.id. */
export type ClaimDetailBlob = {
  dexieId: number
  detailId: string
  sessionId: string
  data: Uint8Array
  mime: string
}

export type AddClaimDetailInput = {
  id: string
  sessionId: string
  markerId: string
  data: Uint8Array
  mime: string
}

export async function addClaimDetail(input: AddClaimDetailInput): Promise<void> {
  const { id, sessionId, markerId, data, mime } = input

  try {
    const now = new Date()
    await dexie.transaction("rw", [dexie.claimDetails, dexie.claimDetailBlobs], async () => {
      await dexie.claimDetails.add({ id, sessionId, markerId, createdAt: now, updatedAt: now, hasData: true })
      await dexie.claimDetailBlobs.add({ detailId: id, sessionId, data, mime })
    })
  } catch (error) {
    logDexieError(error)
  }
}

export async function getClaimDetailsByMarker(markerId: string, includeDeleted = false): Promise<ClaimDetailLocal[]> {
  try {
    const details = await dexie.claimDetails.where("markerId").equals(markerId).sortBy("createdAt")
    return includeDeleted ? details : details.filter((detail) => !detail.deleted)
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function getClaimDetailsBySession(sessionId: string, includeDeleted = false): Promise<ClaimDetailLocal[]> {
  try {
    const details = await dexie.claimDetails.where("sessionId").equals(sessionId).toArray()
    return includeDeleted ? details : details.filter((detail) => !detail.deleted)
  } catch (error) {
    logDexieError(error)
    return []
  }
}

/** Details not yet uploaded (not synced, not deleted, not failed) — drives the background uploader */
export async function getPendingClaimDetailsForUpload(sessionId: string): Promise<ClaimDetailLocal[]> {
  try {
    return await dexie.claimDetails
      .where("sessionId")
      .equals(sessionId)
      .and((detail) => !detail.uploadedAt && !detail.deleted && !detail.uploadFailed)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

/** Load the binary payload for a single detail (lazy — only when rendering the image). */
export async function getClaimDetailBlob(detailId: string): Promise<ClaimDetailBlob | undefined> {
  try {
    return await dexie.claimDetailBlobs.where("detailId").equals(detailId).first()
  } catch (error) {
    logDexieError(error)
  }
}

export async function markClaimDetailUploaded(id: string): Promise<void> {
  try {
    await dexie.claimDetails.where("id").equals(id).modify({ uploadedAt: new Date() })
  } catch (error) {
    logDexieError(error)
  }
}

export async function markClaimDetailUploadFailed(id: string): Promise<void> {
  try {
    await dexie.claimDetails.where("id").equals(id).modify({ uploadFailed: true })
  } catch (error) {
    logDexieError(error)
  }
}

// Soft-delete the metadata (tombstone for a future sync) but hard-delete the binary to free storage
export async function softDeleteClaimDetail(id: string): Promise<void> {
  try {
    logger.info(`[claim] Soft-deleting claim detail ${id} locally`)
    await dexie.transaction("rw", [dexie.claimDetails, dexie.claimDetailBlobs], async () => {
      await dexie.claimDetails.where("id").equals(id).modify({ deleted: true, updatedAt: new Date() })
      await dexie.claimDetailBlobs.where("detailId").equals(id).delete()
    })
  } catch (error) {
    logDexieError(error)
  }
}

export async function hardDeleteClaimDetail(id: string): Promise<void> {
  try {
    await dexie.claimDetails.where("id").equals(id).delete()
  } catch (error) {
    logDexieError(error)
  }
}

/** Uploaded details, deleted locally, that still need their deletion synced to the server */
export async function getPendingClaimDetailDeletions(sessionId: string): Promise<ClaimDetailLocal[]> {
  try {
    return await dexie.claimDetails
      .where("sessionId")
      .equals(sessionId)
      .and((detail) => detail.deleted === true && !!detail.uploadedAt && !detail.deleteFailed)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function markClaimDetailDeleteFailed(id: string): Promise<void> {
  try {
    await dexie.claimDetails.where("id").equals(id).modify({ deleteFailed: true })
  } catch (error) {
    logDexieError(error)
  }
}
